import { msalInstance } from '../authConfig';

// Get API base URL from environment variables with fallback
const getApiBaseUrl = () => {
  // Always prefer localhost if it's available (for development)
  if (import.meta.env.DEV || window.location.hostname === 'localhost') {
    return 'http://localhost:8080';
  }
  
  // In production, use the environment variable, otherwise fall back to relative path
  return import.meta.env.VITE_API_BASE_URL || '';
};

// Build full API URL with cache busting
export const buildApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  let fullUrl;
  // If endpoint already starts with /api, don't add it again
  if (cleanEndpoint.startsWith('/api')) {
    fullUrl = `${baseUrl}${cleanEndpoint}`;
  } else {
    fullUrl = `${baseUrl}/api${cleanEndpoint}`;
  }
  
  // Add cache busting parameter
  const separator = fullUrl.includes('?') ? '&' : '?';
  return `${fullUrl}${separator}_t=${Date.now()}`;
};

/**
 * Authenticated fetch utility that automatically includes MSAL token
 */
export const authenticatedFetch = async (url, options = {}) => {
  try {
    
    // Get the current account
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.error('❌ No authenticated user found');
      throw new Error('No authenticated user found');
    }

    // Acquire token silently
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: accounts[0]
    });

    // Build full URL if it's a relative path
    const fullUrl = url.startsWith('http') ? url : buildApiUrl(url);

    // Prepare headers with authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokenResponse.accessToken}`,
      ...options.headers
    };

    // Make the authenticated request
    const response = await fetch(fullUrl, {
      ...options,
      headers
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Response error:', errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    return response;
  } catch (error) {
    console.error('❌ Authenticated fetch error:', error);
    
    // Add more specific error information
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('🔧 This appears to be a network connectivity issue. Check:');
      console.error('   1. Backend server is running');
      console.error('   2. CORS configuration');
      console.error('   3. Network connectivity');
      console.error('   4. API URL is correct');
      
      // If we're not already using localhost and this is a network error, try localhost fallback
      if (!url.includes('localhost') && window.location.hostname === 'localhost') {
        const localhostUrl = url.replace(/https?:\/\/[^\/]+/, 'http://localhost:8080');
        return authenticatedFetch(localhostUrl, options);
      }
    }
    
    throw error;
  }
};

/**
 * Authenticated fetch that returns JSON data
 */
export const authenticatedFetchJson = async (url, options = {}) => {
  const response = await authenticatedFetch(url, options);
  return response.json();
};

/**
 * API utility object with common HTTP methods
 */
export const api = {
  get: async (url, options = {}) => {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      ...options
    });
    return response.json();
  },

  post: async (url, data = null, options = {}) => {
    const response = await authenticatedFetch(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
      ...options
    });
    return response.json();
  },

  put: async (url, data = null, options = {}) => {
    const response = await authenticatedFetch(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
      ...options
    });
    return response.json();
  },

  delete: async (url, options = {}) => {
    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      ...options
    });
    return response.json();
  },

  patch: async (url, data = null, options = {}) => {
    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null,
      ...options
    });
    return response.json();
  }
};